import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { message } from 'ant-design-vue'
import { useStorage } from '@vueuse/core'
import { useUserStore } from '@/store/user'
import {
  login as loginApi,
  loginBySms as loginBySmsApi,
  logoutAccount,
  register as registerApi,
  registerByPhone as registerByPhoneApi,
  resetPassword as resetPasswordApi,
} from '@/services/user'
import { encrypt, decrypt } from '@/utils/jsencrypt'
import {
  buildAuthRoute,
  completeExtensionAuth,
  isExtensionAuthRequest,
} from '@/utils/extensionAuth'

interface LoginForm {
  username: string
  password: string
  rememberMe?: boolean
}

interface SmsLoginForm {
  phone: string
  verifyCode: string
  agreeTerms: boolean
  agreePrivacy: boolean
  termsVersion: string
  privacyVersion: string
}

interface RegisterForm {
  name: string
  username: string
  email: string
  password: string
  confirmPassword: string
  verifyCode: string
  agreeTerms: boolean
  agreePrivacy: boolean
  termsVersion: string
  privacyVersion: string
}

interface PhoneRegisterForm {
  phone: string
  password: string
  confirmPassword: string
  verifyCode: string
  agreeTerms: boolean
  agreePrivacy: boolean
  termsVersion: string
  privacyVersion: string
}

interface ForgetPasswordForm {
  email: string
  verificationCode: string
  newPassword: string
  confirmPassword: string
}

function getObjectValue(source: unknown, key: string): unknown {
  if (!source || typeof source !== 'object') {
    return undefined
  }
  return (source as Record<string, unknown>)[key]
}

function extractAuthToken(payload: unknown): string {
  const data = getObjectValue(payload, 'data')
  const candidates = [
    getObjectValue(data, 'token'),
    getObjectValue(data, 'accessToken'),
    getObjectValue(data, 'access_token'),
    getObjectValue(payload, 'token'),
    getObjectValue(payload, 'accessToken'),
    getObjectValue(payload, 'access_token'),
  ]

  const token = candidates.find(
    (value): value is string => typeof value === 'string' && !!value.trim()
  )
  return token?.trim() || ''
}

function authErrorMessage(error: unknown, fallback: string): string {
  const response = getObjectValue(error, 'response')
  const data = getObjectValue(response, 'data')
  const candidates = [
    getObjectValue(data, 'message'),
    getObjectValue(data, 'msg'),
    getObjectValue(error, 'message'),
  ]
  const resolved = candidates.find((value): value is string => typeof value === 'string' && !!value.trim())
  return resolved?.trim() || fallback
}

export function useAuth() {
  const router = useRouter()
  const route = useRoute()
  const userStore = useUserStore()
  const loading = ref(false)

  const rememberedUsername = useStorage('username', '', localStorage)
  const rememberedPassword = useStorage('rememberMe', '', localStorage)

  const finishLogin = async (res: any, remember?: LoginForm) => {
    if (res.code !== 200) {
      message.error(res.message || '登录失败')
      return false
    }
    userStore.setUserInfo(res.data as any)
    const token = extractAuthToken(res)
    if (token) userStore.setToken(token)
    if (remember?.rememberMe) {
      rememberedUsername.value = remember.username
      const encryptedPassword = encrypt(remember.password)
      if (encryptedPassword) rememberedPassword.value = encryptedPassword
    } else {
      rememberedUsername.value = ''
      rememberedPassword.value = ''
    }
    if (token && (await completeExtensionAuth(route.query, token))) {
      message.success('登录成功，正在返回 AFSA Stellar')
      return true
    }
    message.success('登录成功！')
    router.push((route.query.redirect as string) || '/dashboard')
    return true
  }

  // 登录
  const login = async (form: LoginForm) => {
    loading.value = true
    try {
      const res = await loginApi({
        username: form.username,
        password: form.password,
      })

      return await finishLogin(res, form)
    } catch (error) {
      message.error(authErrorMessage(error, '登录失败'))
      return false
    } finally {
      loading.value = false
    }
  }

  const loginBySms = async (form: SmsLoginForm) => {
    loading.value = true
    try {
      return await finishLogin(await loginBySmsApi(form))
    } catch (error) {
      message.error(authErrorMessage(error, '登录失败'))
      return false
    } finally {
      loading.value = false
    }
  }

  // 注册
  const register = async (form: RegisterForm) => {
    loading.value = true
    try {
      const res = await registerApi({
        name: form.name,
        username: form.username,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
        verifyCode: form.verifyCode,
        agreeTerms: form.agreeTerms,
        agreePrivacy: form.agreePrivacy,
        termsVersion: form.termsVersion,
        privacyVersion: form.privacyVersion,
      })

      if (res.code === 200) {
        const t = extractAuthToken(res)
        if (t) {
          userStore.setUserInfo(res.data as any)
          userStore.setToken(t)
          if (await completeExtensionAuth(route.query, t)) {
            message.success('注册成功，正在返回 AFSA Stellar')
            return true
          }
        }

        message.success('注册成功！即将跳转到登录页面...')
        setTimeout(() => {
          router.push(buildAuthRoute('/login', route.query))
        }, 500)
        return true
      } else {
        message.error(res.message || '注册失败')
        return false
      }
    } catch (error) {
      message.error(authErrorMessage(error, '注册失败'))
      return false
    } finally {
      loading.value = false
    }
  }

  const registerByPhone = async (form: PhoneRegisterForm) => {
    loading.value = true
    try {
      const res = await registerByPhoneApi(form)
      if (res.code !== 200) {
        message.error(res.message || '注册失败')
        return false
      }
      const token = extractAuthToken(res)
      if (token) {
        userStore.setUserInfo(res.data as any)
        userStore.setToken(token)
      }
      message.success('注册成功！')
      router.push((route.query.redirect as string) || '/dashboard')
      return true
    } catch (error) {
      message.error(authErrorMessage(error, '注册失败'))
      return false
    } finally {
      loading.value = false
    }
  }

  // 重置密码
  const resetPassword = async (form: ForgetPasswordForm) => {
    loading.value = true
    try {
      const res = await resetPasswordApi({
        email: form.email,
        code: form.verificationCode,
        password: form.newPassword,
      })

      if (res.code === 200) {
        message.success('密码重置成功！即将跳转到登录页面...')
        setTimeout(() => {
          router.push('/login')
        }, 500)
        return true
      } else {
        message.error(res.message || '密码重置失败')
        return false
      }
    } catch (error) {
      message.error(authErrorMessage(error, '密码重置失败'))
      return false
    } finally {
      loading.value = false
    }
  }

  const getRememberedCredentials = () => {
    const username = rememberedUsername.value
    const encryptedPassword = rememberedPassword.value
    const password = encryptedPassword ? decrypt(encryptedPassword) : ''

    return {
      username,
      password: typeof password === 'string' ? password : '',
      rememberMe: !!encryptedPassword,
    }
  }

  const logout = async () => {
    try {
      await logoutAccount()
    } catch (error) {
      console.warn('账号中心退出接口调用失败，继续清理本地登录态', error)
    }
    userStore.clearUserInfo()
    userStore.clearToken()
    // 安全加固：清除安全Token Cookie（后端也会处理）
    document.cookie = 'auth_token=; path=/; max-age=0'
    // 兼容旧版本，清除username Cookie
    document.cookie = 'username=; path=/; max-age=0'
    router.push('/login')
  }

  return {
    loading,
    login,
    loginBySms,
    register,
    registerByPhone,
    resetPassword,
    getRememberedCredentials,
    isExtensionAuthRequest: () => isExtensionAuthRequest(route.query),
    buildAuthRoute: (path: '/login' | '/register') => buildAuthRoute(path, route.query),
    logout,
  }
}
